'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Navbar from '@/components/Navbar';
import FeedFilterBar, { FeedFilters } from '@/app/home-feed/components/FeedFilterBar';
import TradeCard, { TradeCardData } from '@/app/home-feed/components/TradeCard';
import TrendingSidebar from '@/app/home-feed/components/TrendingSidebar';
import FeedSkeleton from '@/app/home-feed/components/FeedSkeleton';
import TradePostModal from '@/app/home-feed/components/TradePostModal';
import SponsoredSlot from '@/components/common/SponsoredSlot';
import { RefreshCw, Compass, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useLang } from '@/lib/i18n/LanguageContext';

const FEED_LIMIT = 20;

const defaultFilters: FeedFilters = {
  tab: 'all',
  ideaType: 'ALL',
  market: 'ALL',
  timeframe: 'ALL',
  direction: 'ALL',
  status: 'ALL',
  sort: 'trending',
  search: '',
};

export default function DiscoverPage() {
  const { t } = useLang();
  const [filters, setFilters] = useState<FeedFilters>(defaultFilters);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [trades, setTrades] = useState<TradeCardData[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | undefined>(undefined);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const seenIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id);
    });
  }, []);

  const fetchTrades = useCallback(
    async (currentFilters: FeedFilters, currentOffset: number, append: boolean) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setFetchError(null);
      }

      try {
        const params = new URLSearchParams();
        if (currentFilters.market !== 'ALL') params.set('market', currentFilters.market);
        if (currentFilters.timeframe !== 'ALL') params.set('timeframe', currentFilters.timeframe);
        if (currentFilters.direction !== 'ALL') params.set('direction', currentFilters.direction);
        if (currentFilters.status !== 'ALL') params.set('status', currentFilters.status);
        if (currentFilters.ideaType !== 'ALL') params.set('ideaType', currentFilters.ideaType);
        if (currentFilters.search) params.set('search', currentFilters.search);
        params.set('sort', currentFilters.sort);
        params.set('limit', String(FEED_LIMIT));
        params.set('offset', String(currentOffset));

        const res = await fetch(`/api/trades?${params.toString()}`);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || 'Failed to load feed');
        }
        const { trades: fetchedTrades, hasMore: more } = await res.json();
        const result: TradeCardData[] = fetchedTrades || [];

        if (append) {
          const newTrades = result.filter((t) => !seenIdsRef.current.has(t.id));
          newTrades.forEach((t) => seenIdsRef.current.add(t.id));
          setTrades((prev) => [...prev, ...newTrades]);
        } else {
          seenIdsRef.current = new Set(result.map((t) => t.id));
          setTrades(result);
        }

        setHasMore(more ?? false);
        setOffset(currentOffset + result.length);
      } catch (err: any) {
        if (!append) {
          setFetchError(err.message || 'Failed to load trades');
          setTrades([]);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    seenIdsRef.current = new Set();
    setOffset(0);
    setHasMore(false);
    fetchTrades(filters, 0, false);
  }, [filters, fetchTrades]);

  function handleFilterChange(partial: Partial<FeedFilters>) {
    // Analyses never settle, so a status filter and Analyses are mutually exclusive (see FeedPage).
    setFilters((prev) => {
      const next = { ...prev, ...partial };
      if (partial.ideaType === 'ANALYSIS') next.status = 'ALL';
      else if (prev.ideaType === 'ANALYSIS' && partial.status && partial.status !== 'ALL') next.ideaType = 'SIGNAL';
      return next;
    });
  }

  function handleStatusUpdated(tradeId: string, status: any) {
    setTrades((prev) => prev.map((t) => (t.id === tradeId ? { ...t, status } : t)));
  }

  function handleLoadMore() {
    fetchTrades(filters, offset, true);
  }

  function handleRefresh() {
    seenIdsRef.current = new Set();
    setOffset(0);
    setHasMore(false);
    fetchTrades(filters, 0, false);
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-base)' }}>
      <Navbar onPostTrade={() => setModalOpen(true)} />

      <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 pb-28 md:pb-6">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-muted)' }}>
            <Compass size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{t('Discover')}</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Trending trade ideas from the community')}</p>
          </div>
        </div>

        <div className="flex flex-col xl:flex-row gap-6">
          {/* Main feed column */}
          <div className="flex-1 min-w-0 max-w-2xl xl:max-w-none">
            {/* One non-aggressive sponsored slot near the top of results (12h frequency cap) */}
            <SponsoredSlot location="discover" frequencyCapHours={12} className="mb-4" />
            <div className="rounded-t-[20px] overflow-hidden"
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderBottom: 'none' }}>
              <FeedFilterBar filters={filters} onChange={handleFilterChange} />
            </div>

            <div className="rounded-b-[20px] overflow-hidden" style={{ border: '1px solid var(--border)', borderTop: 'none' }}>
              {loading ? (
                <div className="p-4"><FeedSkeleton /></div>
              ) : fetchError ? (
                <div className="flex flex-col items-center justify-center py-20 px-6 text-center" style={{ background: 'var(--bg-card)' }}>
                  <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{t('Failed to load')}</h3>
                  <p className="text-sm max-w-xs mb-4" style={{ color: 'var(--text-muted)' }}>{t(fetchError)}</p>
                  <button onClick={handleRefresh} className="btn-primary px-5 py-2.5 text-sm flex items-center gap-2">
                    <RefreshCw size={14} />{t('Retry')}
                  </button>
                </div>
              ) : trades.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 px-6 text-center" style={{ background: 'var(--bg-card)' }}>
                  <Compass size={32} className="mb-4" style={{ color: 'var(--accent)' }} />
                  <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>{t('Nothing to discover yet')}</h3>
                  <p className="text-sm max-w-xs" style={{ color: 'var(--text-muted)' }}>{t('Be the first to post a trade idea, or adjust your filters.')}</p>
                  <button onClick={() => setFilters(defaultFilters)} className="btn-primary mt-4 px-5 py-2.5 text-sm">{t('Clear Filters')}</button>
                </div>
              ) : (
                <div className="divide-y" style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}>
                  {trades.map((trade) => (
                    <div key={trade.id} className="p-4">
                      <TradeCard trade={trade} currentUserId={currentUserId} onStatusUpdated={handleStatusUpdated} />
                    </div>
                  ))}
                  <div className="flex flex-col items-center justify-center p-5 gap-3" style={{ background: 'var(--bg-card)' }}>
                    {hasMore ? (
                      <button onClick={handleLoadMore} disabled={loadingMore}
                        className="btn-ghost flex items-center gap-2 px-6 py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                        {loadingMore ? (
                          <><Loader2 size={14} className="animate-spin" />{t('Loading…')}</>
                        ) : (
                          <><ChevronDownIcon size={14} />{t('Load more')}</>
                        )}
                      </button>
                    ) : (
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{t("You've reached the end")}</p>
                    )}
                    <button className="btn-ghost flex items-center gap-2 px-4 py-2 text-xs" onClick={handleRefresh}>
                      <RefreshCw size={12} />{t('Refresh')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right sidebar */}
          <div className="w-full xl:w-80 2xl:w-96 shrink-0">
            <div className="sticky top-20 flex flex-col gap-4">
              <TrendingSidebar />
            </div>
          </div>
        </div>
      </main>

      <TradePostModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onTradePosted={(trade) => setTrades((prev) => [trade, ...prev])}
      />

    </div>
  );
}

function ChevronDownIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
